/**
 * Meta Pixel — loaded only after the visitor accepts marketing cookies.
 *
 * KVKK requires opt-in for marketing tracking, so nothing from Meta is
 * fetched before `hasMarketingConsent()` is true, and nothing at all when the
 * build has no VITE_META_PIXEL_ID. The backend sends the same conversions
 * through the Conversions API (services/metaConversionsService.ts); both
 * halves carry the same event id so Meta counts each conversion once.
 *
 * PageView is fired by hand on public pages only (see MetaPixelPageViews):
 * app screens are not ad audiences, and their URLs are nobody's business.
 */

import { hasMarketingConsent } from "./cookieConsent";
import { readAcquisition } from "./acquisition";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export type MetaEventName = "PageView" | "Lead" | "CompleteRegistration" | "InitiateCheckout" | "Purchase";

const pixelId = (): string => {
  const id = String(import.meta.env.VITE_META_PIXEL_ID ?? "").trim();
  return /^\d{5,20}$/.test(id) ? id : "";
};

export const isMetaPixelConfigured = (): boolean => pixelId() !== "";

/** Fired on window when the visitor accepts, so the current page can be counted. */
export const MARKETING_CONSENT_EVENT = "humo:marketing-consent";

let active = false;

/** Meta's base snippet, minus the inline PageView. */
function installFbq(): Fbq {
  if (window.fbq) return window.fbq;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
  return fbq;
}

/**
 * Load the Pixel if there is an id and consent. Safe to call repeatedly:
 * at boot, and again from the cookie banner's Accept.
 */
export function initMetaPixel(): boolean {
  if (typeof window === "undefined") return false;
  const id = pixelId();
  if (!id || !hasMarketingConsent()) return false;
  if (active) return true;

  const fbq = installFbq();
  // The SPA fires its own PageViews; the Pixel's history listener would double them.
  fbq.disablePushState = true;
  fbq("consent", "grant");
  fbq("init", id);
  active = true;
  return true;
}

/** Consent withdrawn: stop sending. The script stays until the next reload. */
export function disableMetaPixel(): void {
  if (typeof window === "undefined" || !window.fbq) return;
  window.fbq("consent", "revoke");
  active = false;
}

export const isMetaPixelActive = (): boolean => active;

/** An id the browser event and the Conversions API event share. */
export function newMetaEventId(name: MetaEventName): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${name}.${random}`;
}

export function trackMetaEvent(name: MetaEventName, params?: Record<string, unknown>, eventId?: string): void {
  if (!active || !window.fbq) return;
  if (eventId) window.fbq("track", name, params ?? {}, { eventID: eventId });
  else window.fbq("track", name, params ?? {});
}

/**
 * Give the Pixel a moment to send before a full-page navigation (signup
 * reloads the app right after success). Resolves at once when inactive.
 */
export const flushMetaPixel = (ms = 400): Promise<void> =>
  active ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

const readCookie = (name: string): string | undefined => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
};

/** What the backend needs to send the matching Conversions API event. */
export interface MetaRequestContext {
  consent: true;
  event_id?: string;
  fbp?: string;
  fbc?: string;
  event_source_url: string;
}

/**
 * Sent next to signup / checkout requests. undefined without marketing
 * consent — the backend then sends nothing to Meta either.
 */
export function readMetaContext(eventId?: string): MetaRequestContext | undefined {
  if (typeof window === "undefined" || !hasMarketingConsent()) return undefined;

  // The Pixel writes _fbc only if the ad click's fbclid is in the URL when it
  // loads; a visitor who accepted cookies a page later still has it from the
  // landing page.
  let fbc = readCookie("_fbc");
  if (!fbc) {
    const acquisition = readAcquisition();
    if (acquisition?.fbclid) fbc = `fb.1.${acquisition.fbclid_at ?? Date.now()}.${acquisition.fbclid}`;
  }
  const fbp = readCookie("_fbp");

  return {
    consent: true,
    ...(eventId ? { event_id: eventId } : {}),
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
    // Path only: query strings can carry e-mails or tokens.
    event_source_url: `${window.location.origin}${window.location.pathname}`
  };
}
